import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { searchCustomers, createCustomer, awardPoints } from "./cashierApi";
import { newIdempotencyKey } from "./idempotency";
import RedeemList from "./RedeemList";

/**
 * The till's "find anyone" panel — search a guest, then redeem for them or add
 * points for their bill.
 *
 * It began as Add Points only. It is also the FALLBACK ROUTE TO A REDEMPTION,
 * and that half matters: the board only lists guests who ordered today or who
 * tapped "I'm at the counter" on their card. A regular who did neither — no
 * phone to hand, card not opened, or simply someone who walked up and said "I
 * think I've got a free coffee" — has to be servable too, and the cashier is
 * the one who knows they are standing there. So a searched guest gets the same
 * `Ready: {reward} [Redeem]` rows as one on the board, from the same component.
 *
 * ADD POINTS — for purchases that never went through the QR order flow, which
 * at most venues is nearly all of them. Find the guest, key in the FULL BILL
 * TOTAL, award at the venue's own spend rate.
 *
 * The whole bill, not a remainder: `purchasePointsFor()` deliberately has no
 * `points_per_order` floor, so what the cashier types is exactly what is
 * scaled, and asking a till to subtract the QR part of a mixed bill in their
 * head is how the wrong number gets typed. Where a guest DOES have an open QR
 * ticket, the guest strip warns before the tap instead — Mark paid is about to
 * credit that part, and paying it twice is the failure mode worth preventing.
 *
 * A till award opens the F-1 redemption gate exactly like marking a QR order
 * paid does. Without that, a venue running entirely on counter sales would mint
 * balances no guest could ever redeem.
 *
 * Double-award protection is two-layered and both layers matter at a till:
 *  • a per-award idempotency key, regenerated only after a SUCCESSFUL award, so
 *    a double-tap or a re-submit after a slow response reuses it and the server
 *    returns the original ledger row;
 *  • the server's duplicate-window 409, surfaced here as an explicit confirm
 *    rather than swallowed — a genuine repeat sale is legitimate, so the cashier
 *    decides, but never by accident.
 *
 * `presetCustomer` lets the guest strip hand a guest straight in. Someone whose
 * name is already on the board should never have to be searched for again.
 */
export default function AddPointsPanel({ token, currencySymbol, rate, rewards, presetCustomer, onAwarded, onRedeemed }) {
  const { t } = useTranslation();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [customer, setCustomer] = useState(null);

  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);       // { points, balance, name }
  const [confirmDup, setConfirmDup] = useState(false);

  const [quickAdd, setQuickAdd] = useState(false);
  const [qaName, setQaName] = useState("");
  const [qaPhone, setQaPhone] = useState("");

  // Stable per-award key — regenerated ONLY after a successful award, so every
  // retry of the same award carries the same key and can't double-credit.
  const idemKey = useRef(newIdempotencyKey());
  const abortRef = useRef(null);
  const rootRef = useRef(null);

  const rateOff = !(Number(rate) > 0);
  const amountNum = Number(amount);
  const amountValid = amount !== "" && Number.isFinite(amountNum) && amountNum > 0;
  const preview = amountValid && !rateOff ? Math.floor(amountNum * Number(rate)) : 0;

  /* ── debounced search ──────────────────────────────────────────────────── */
  useEffect(() => {
    const q = query.trim();
    if (customer || q.length < 2) { setResults([]); setSearched(false); return undefined; }

    const timer = setTimeout(() => {
      abortRef.current?.abort();
      const ctl = new AbortController();
      abortRef.current = ctl;
      setSearching(true);
      searchCustomers(token, q, ctl.signal)
        .then((d) => { setResults(d?.customers || []); setSearched(true); })
        .catch((e) => { if (e?.code !== "ERR_CANCELED") { setResults([]); setSearched(true); } })
        .finally(() => setSearching(false));
    }, 300);

    return () => clearTimeout(timer);
  }, [query, customer, token]);

  useEffect(() => () => abortRef.current?.abort(), []);

  // Handed a guest by the board's guest strip. Scrolled into view because on a
  // till tablet the panel is usually off-screen when the tap happens, and a
  // selection the cashier can't see reads as the tap having done nothing.
  useEffect(() => {
    if (!presetCustomer) return;
    setCustomer(presetCustomer);
    setResults([]); setQuery(""); setErr(""); setDone(null); setConfirmDup(false); setQuickAdd(false);
    try { rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (_) {}
  }, [presetCustomer]);

  const pick = (c) => {
    setCustomer(c); setResults([]); setQuery(""); setErr(""); setDone(null); setConfirmDup(false);
  };

  const clearCustomer = () => {
    setCustomer(null); setAmount(""); setErr(""); setDone(null); setConfirmDup(false); setQuickAdd(false);
  };

  const submitQuickAdd = async (e) => {
    e.preventDefault();
    if (!qaName.trim() || !qaPhone.trim()) return;
    setBusy(true); setErr("");
    try {
      const res = await createCustomer(token, qaName.trim(), qaPhone.trim());
      pick(res.customer);
      setQuickAdd(false); setQaName(""); setQaPhone("");
    } catch (e2) {
      setErr(e2?.response?.data?.error === "invalid_phone" ? t("cashier.pts.err_phone") : t("cashier.pts.err_generic"));
    } finally {
      setBusy(false);
    }
  };

  const submitAward = useCallback(async (force) => {
    if (!customer || !amountValid || busy) return;
    setBusy(true); setErr(""); setConfirmDup(false);
    try {
      const res = await awardPoints(token, {
        customerId: customer.id,
        amount: amountNum,
        idempotencyKey: idemKey.current,
        force,
      });
      setDone({ points: res.points, balance: res.balance, name: customer.name, awarded: res.awarded });
      // New key only now — the previous award is banked and must never be
      // reachable again by a stray retry.
      idemKey.current = newIdempotencyKey();
      setAmount("");
      setCustomer((c) => (c ? { ...c, balance: res.balance } : c));
      onAwarded?.(res);
    } catch (e) {
      const code = e?.response?.data?.error;
      if (code === "duplicate_recent") setConfirmDup(true);
      else if (code === "spend_rate_disabled") setErr(t("cashier.pts.err_rate_off"));
      else if (code === "amount_too_small") setErr(t("cashier.pts.err_small"));
      else if (code === "customer_not_found") { setErr(t("cashier.pts.err_generic")); clearCustomer(); }
      else setErr(t("cashier.pts.err_generic"));
    } finally {
      setBusy(false);
    }
  }, [customer, amountValid, amountNum, busy, token, onAwarded, t]);

  return (
    <div className="cds-pts" ref={rootRef}>
      <div className="cds-pts-head">
        <span className="ic">🔎</span>
        <div>
          <div className="ttl">{t("cashier.pts.title")}</div>
          <div className="sub">{t("cashier.pts.sub")}</div>
        </div>
      </div>

      {/* The search is NEVER gated on the spend rate. It used to be — the whole
          body sat behind `rateOff` — and that was fine while this panel only
          awarded points. It is now also the fallback route to a REDEMPTION, and
          redeeming has nothing to do with whether the venue opted into
          spend-scaling: a venue with no rate still has rewards, and its guests
          still walk up to collect them. Only the AMOUNT half is gated now. */}
        <div className="cds-pts-body">
          {/* ── who ─────────────────────────────────────────────────────── */}
          {customer ? (
            <div className="cds-pts-cust">
              <span className="av">{(customer.name || "?").trim().charAt(0).toUpperCase()}</span>
              <div className="who">
                <div className="nm">{customer.name}</div>
                <div className="mt">
                  <span className="mono">{customer.phone}</span>
                  <span>· {t("cashier.pts.balance", { points: customer.balance ?? 0 })}</span>
                </div>
              </div>
              <button type="button" className="cds-pts-x" onClick={clearCustomer}
                aria-label={t("cashier.pts.change")}>✕</button>
            </div>
          ) : (
            <div className="cds-pts-search">
              <input
                className="cds-pts-in"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("cashier.pts.search_ph")}
                aria-label={t("cashier.pts.search_ph")}
                autoComplete="off"
              />
              {searching && <span className="cds-spin cds-pts-spin" />}

              {results.length > 0 && (
                <ul className="cds-pts-list">
                  {results.map((c) => (
                    <li key={c.id}>
                      <button type="button" onClick={() => pick(c)}>
                        <span className="nm">{c.name || t("cashier.pts.no_name")}</span>
                        <span className="ph mono">{c.phone}</span>
                        <span className="bal">{t("cashier.pts.pts", { points: c.balance ?? 0 })}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {searched && !searching && results.length === 0 && !quickAdd && (
                <div className="cds-pts-none">
                  <span>{t("cashier.pts.none")}</span>
                  <button type="button" onClick={() => {
                    // Seed whichever field the cashier was clearly typing.
                    const q = query.trim();
                    if (/\d/.test(q)) setQaPhone(q); else setQaName(q);
                    setQuickAdd(true);
                  }}>{t("cashier.pts.quick_add")}</button>
                </div>
              )}

              {quickAdd && (
                <form className="cds-pts-qa" onSubmit={submitQuickAdd}>
                  <input className="cds-pts-in" value={qaName} onChange={(e) => setQaName(e.target.value)}
                    placeholder={t("cashier.pts.qa_name")} aria-label={t("cashier.pts.qa_name")} maxLength={100} />
                  <input className="cds-pts-in mono" value={qaPhone} onChange={(e) => setQaPhone(e.target.value)}
                    placeholder={t("cashier.pts.qa_phone")} aria-label={t("cashier.pts.qa_phone")}
                    dir="ltr" inputMode="tel" maxLength={32} />
                  <div className="cds-pts-qa-btns">
                    <button type="submit" className="cds-pts-go" disabled={busy || !qaName.trim() || !qaPhone.trim()}>
                      {busy ? <span className="cds-spin" /> : t("cashier.pts.qa_save")}
                    </button>
                    <button type="button" className="cds-pts-ghost" onClick={() => setQuickAdd(false)}>
                      {t("cashier.pts.cancel")}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* ── redeem — the fallback route, same component as the board ── */}
          {customer && (
            <div className="cds-pts-redeem">
              <div className="cds-pts-sec">{t("cashier.redeem.section")}</div>
              <RedeemList
                token={token}
                guest={customer}
                rewards={rewards}
                onRedeemed={(res) => {
                  // Keep the selected chip honest — the balance just moved.
                  setCustomer((c) => (c && res?.customer
                    ? { ...c, balance: res.customer.balance, can_redeem: res.customer.can_redeem }
                    : c));
                  onRedeemed?.(res);
                }}
              />
            </div>
          )}

          {/* ── how much ────────────────────────────────────────────────── */}
          {customer && rateOff && (
            <div className="cds-pts-off">
              <b>{t("cashier.pts.rate_off_title")}</b>
              <span>{t("cashier.pts.rate_off_body")}</span>
            </div>
          )}

          {customer && !rateOff && (
            <>
              <div className="cds-pts-sec">{t("cashier.pts.section")}</div>
              <div className="cds-pts-amt-lab">{t("cashier.pts.bill_label")}</div>
              <div className="cds-pts-amt">
                <span className="cur">{currencySymbol}</span>
                <input
                  className="cds-pts-in amt mono"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1"))}
                  placeholder={t("cashier.pts.amount_ph")}
                  aria-label={t("cashier.pts.amount_ph")}
                  inputMode="decimal"
                  dir="ltr"
                  autoFocus
                />
              </div>

              <div className={`cds-pts-prev${amountValid ? " on" : ""}`}>
                {amountValid
                  ? t("cashier.pts.preview", { amount: trimNum(amountNum), currency: currencySymbol, points: preview })
                  : t("cashier.pts.preview_hint", { rate: trimNum(Number(rate)), currency: currencySymbol })}
              </div>

              {err ? <div className="cds-pts-err">{err}</div> : null}

              {confirmDup ? (
                <div className="cds-pts-dup">
                  <span>{t("cashier.pts.dup_warn")}</span>
                  <div className="cds-pts-qa-btns">
                    <button type="button" className="cds-pts-go" disabled={busy} onClick={() => submitAward(true)}>
                      {busy ? <span className="cds-spin" /> : t("cashier.pts.dup_confirm")}
                    </button>
                    <button type="button" className="cds-pts-ghost" onClick={() => setConfirmDup(false)}>
                      {t("cashier.pts.cancel")}
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" className="cds-pts-award" disabled={!amountValid || busy || preview <= 0}
                  onClick={() => submitAward(false)}>
                  {busy ? <><span className="cds-spin" /> {t("cashier.processing")}</>
                        : t("cashier.pts.award", { points: preview })}
                </button>
              )}
            </>
          )}

          {done ? (
            <div className="cds-pts-done">
              ✓ {done.awarded
                ? t("cashier.pts.done", { points: done.points, name: done.name, balance: done.balance })
                : t("cashier.pts.done_already", { name: done.name, balance: done.balance })}
            </div>
          ) : null}
        </div>
    </div>
  );
}

/** 20 → "20", 20.5 → "20.5" — no trailing ".00" in a preview line. */
function trimNum(n) {
  return String(Math.round(n * 100) / 100);
}
